import { useCallback, useState } from 'react';
import type { GetPresignedUrlParams, GetPresignedUrlResponse } from '@/types/fileManagerApiTypes';
import { getConvertedToWebpImages } from '@/utils/getConvertedToWebpImages';
import { useFetch } from '../useFetch';

interface UploadFileDataResult {
  storage_id: string;
  fileExtension: string;
}

interface UseUploadFileDataReturn {
  uploadFileData: (file: File) => Promise<UploadFileDataResult>;
  isLoading: boolean;
  error: unknown;
}

export const useUploadFileData = (): UseUploadFileDataReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { doFetch } = useFetch<GetPresignedUrlParams, GetPresignedUrlResponse>();

  const getPresignedUrl = useCallback(
    async (fileExtension: string) => {
      return doFetch('/api/storage/presigned-url', {
        method: 'POST',
        body: { file_extension: fileExtension },
      }).then((resp) => {
        if (!resp.data.upload_url) {
          throw new Error('Failed to get presigned URL');
        }
        return resp.data;
      });
    },
    [doFetch]
  );

  const putToStorage = useCallback(async (url: string, file: File) => {
    const putResp = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });
    if (!putResp.ok) {
      throw new Error('Failed to upload file to storage');
    }
  }, []);

  const uploadFileData = useCallback(
    async (file: File): Promise<UploadFileDataResult> => {
      setIsLoading(true);
      setError(null);
      try {
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

        const fileToUpload = isPdf ? file : (await getConvertedToWebpImages([file]))[0];

        const fileExtension = fileToUpload.name.split('.').pop()?.toLowerCase() || (isPdf ? 'pdf' : '');

        const presignedData = await getPresignedUrl(fileExtension);
        await putToStorage(presignedData.upload_url, fileToUpload);

        return {
          storage_id: presignedData.storage_id,
          fileExtension: fileExtension,
        };
      } catch (err) {
        setError(err);
        throw new Error('Failed to upload file');
      } finally {
        setIsLoading(false);
      }
    },
    [getPresignedUrl, putToStorage]
  );

  return { uploadFileData, isLoading, error };
};
